// Central place that turns any thrown error into a SAFE, user-facing message.
//
// Why: pos-api builds an error's `.message` from whatever the backend returned
// (data.message / data.error / data.details) — which can be technical, internal,
// or developer-oriented ("Could not reach the POS backend. Start the ordering
// backend there…", a 500 stack, an SQL hint). None of that should ever reach a
// cashier's screen, where a customer might also see it.
//
// safeErrorMessage() logs the real error to the console for debugging, then
// returns only a curated message chosen by HTTP status — falling back to the
// caller's friendly, context-specific default. It NEVER returns raw `error.message`.

// Status → friendly copy. Tone is calm and non-technical.
const STATUS_MESSAGES = {
  0: 'Can’t reach the server. Check your connection and try again.',
  401: 'Your session has expired. Please log in again.',
  403: 'You don’t have permission to do that.',
  404: 'We couldn’t find that. Please refresh and try again.',
  408: 'The request timed out. Check your connection and try again.',
  409: 'That was just updated elsewhere. Please refresh and try again.',
  413: 'That file is too large to upload.',
  429: 'Too many requests right now. Please wait a moment and try again.',
};

const SERVER_ERROR = 'Something went wrong on our end. Please try again in a moment.';
export const GENERIC_ERROR = 'Something went wrong. Please try again.';

/**
 * @param {*} error      The caught error (may carry a numeric `.status`).
 * @param {string} fallback  Friendly, context-specific default to show when the
 *                           status has no specific mapping.
 * @param {object} [options]
 * @param {string} [options.context]  Label for the console log (e.g. 'order-sync').
 * @param {Object<number,string>} [options.statusOverrides]  Per-status copy that
 *        takes priority over the defaults (e.g. login treats 401 as bad credentials).
 * @returns {string} A message that is always safe to display.
 */
export const safeErrorMessage = (error, fallback = GENERIC_ERROR, options = {}) => {
  const { context = 'POS', statusOverrides = {} } = options;

  // Keep the real detail for developers — never for the UI.
  if (error) {
    // eslint-disable-next-line no-console
    console.error(`[${context}]`, error);
  }

  const status = Number(error?.status);

  if (Object.prototype.hasOwnProperty.call(statusOverrides, status)) {
    return statusOverrides[status];
  }
  if (Object.prototype.hasOwnProperty.call(STATUS_MESSAGES, status)) {
    return STATUS_MESSAGES[status];
  }
  if (status >= 500 && status < 600) {
    return SERVER_ERROR;
  }
  // Unknown 4xx, no status, or a plain JS error → the caller's friendly default.
  return fallback;
};
